import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
} from "@angular/core";
import { StripeService } from "../../services/stripe.service";
import { UserService } from "../../services/user.service";
import { Booking } from "../../models/booking.model";
import { ClientService } from "../../services/client.service";
import { Client } from "../../models/client.model";
import { environment } from "../../../../environments/environment";
import { Salon } from "../../models/salon.model";
import { loadStripe } from "@stripe/stripe-js";
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: "salonmonster-stripe-payment",
  templateUrl: "./stripe-payment.component.html",
  styleUrls: ["stripe-payment.component.scss"],
})
export class StripePaymentComponent
  implements AfterViewInit, OnInit, OnChanges
{
  @ViewChild("paymentElement") paymentElementRef: ElementRef;
  @ViewChild("paymentElementChangeCard") paymentElementChangeCardRef: ElementRef;
  @Output("onBookingSave") onBookingSave: EventEmitter<any> =
    new EventEmitter();

  @Input() booking?: Booking;
  @Input() client?: Client;
  @Input() errorOccurredWhileSaving: boolean;

  @Input() mode: String = "booking"; //booking, waitlist, cardrequest
  public clientHasStripeAccount: Boolean = false;
  public noShowPercentage: number;
  @Input() refreshStripe;
  @Input() savingStripe: boolean = false;

  public stripe;
  public elements;
  public cardErrors;
  public newStripeClient;
  public paymentElement;
  public errorMessage;
  public saveCardChecked;
  public cardInputComplete = false;

  public buttonText: string;
  public requirementText: string;
  public clientBookingPayments;
  public salon: Salon;
  public cancellationPolicy: string;
  public stripeReady: boolean = false;
  private stripePromise: Promise<any>;

  public totalPrice;
  public currency;
  public cardInfo = {
    brand: "",
    last4: "",
    exp_month: "",
    exp_year: "",
    funding: "",
  };
  public changeCard;

  public cardExpired: boolean = false;
  constructor(
    private stripeService: StripeService,
    private ref: ChangeDetectorRef,
    public userService: UserService,
    private clientService: ClientService,
    private translateService: TranslateService
  ) {}
  public async ngOnInit() {
    this.salon = this.userService.getSalon();
    if (this.client) {
    } else if (this.booking) {
      if (this.userService.isLoggedIn()) {
        this.checkUserStripeAccount();
      }
      this.clientBookingPayments = this.userService
        .getSalon()
        .getClientBookingPayments();
      this.noShowPercentage = this.userService.getSalon().getNoShowPercentage();

      if (this.mode === "waitlist") {
        this.buttonText = this.translateService.instant('STRIPE.BUTTON_WAITLIST');
        this.requirementText = this.translateService.instant('STRIPE.REQUIREMENT_WAITLIST');
      } else if (this.mode === "cardrequest") {
        this.buttonText = this.translateService.instant('STRIPE.BUTTON_CARD_REQUEST');
        this.requirementText = this.translateService.instant('STRIPE.REQUIREMENT_CARD_REQUEST');
      } else {
        this.buttonText = this.translateService.instant('STRIPE.BUTTON_BOOK');
        this.requirementText = this.translateService.instant('STRIPE.REQUIREMENT_BOOKING');
      }

      this.cancellationPolicy = this.salon.getCancellationPolicy();
    }
  }

  public async ngAfterViewInit() {
    this.loadStripeCustomer();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.userService.isLoggedIn()) {
      this.client = await this.clientService.getClientInfo().toPromise();
      this.checkUserStripeAccount();
    }
    if (changes.errorOccurredWhileSaving) {
      this.savingStripe = false;
    }
  }

  public async checkUserStripeAccount() {
    const client = await this.clientService.getClientInfo().toPromise();
    this.client = client;
    const response = await this.stripeService
      .getClientStripeID({ clientID: client["id"] })
      .toPromise();
    response
      ? (this.clientHasStripeAccount = response)
      : (this.clientHasStripeAccount = false);
    if (this.clientHasStripeAccount) {
      this.loadStripeCustomer();
    } else {
      this.newStripeClient = true;
      this.setupStripeElements("saveCard");
    }
  }

  async loadStripeCustomer() {
    // Load Stripe.js once, but let the customer-card lookup below run every
    // call: this method fires from ngAfterViewInit before we know whether
    // the client has a Stripe account, and again from checkUserStripeAccount
    // once we do.
    if (!this.stripePromise) {
      this.stripePromise = loadStripe(environment.stripePublishableKey);
    }
    this.stripe = await this.stripePromise;

    // Don't initiate stripe when user not logged in
    if (!this.clientHasStripeAccount || !this.userService.isLoggedIn() || !this.client) {
      return;
    }

    this.newStripeClient = false;
    this.cardInfo = await this.stripeService
      .getCustomerCardInfo({ clientID: this.client.getID() })
      .toPromise();

    if (this.cardInfo === undefined) {
      this.newStripeClient = true;
      this.setupStripeElements("saveCard");
    } else {
      const today = new Date();
      if (today.getFullYear() > Number(this.cardInfo.exp_year) || (today.getFullYear() == Number(this.cardInfo.exp_year) && today.getMonth() >  Number(this.cardInfo.exp_month))) {
        this.cardExpired = true;
      }
    }
    this.ref.detectChanges();
  }

  public setupStripeElements(section) {
    this.elements = this.stripe.elements({
      mode: "setup",
      currency: this.salon?.getStripeCurrency() || "usd",
    });
    this.paymentElement = this.elements.create("payment");
    if (section === "changeCard") {
      this.paymentElement.mount(this.paymentElementChangeCardRef.nativeElement);
    } else {
      this.paymentElement.mount(this.paymentElementRef.nativeElement);
    }
    this.paymentElement.on("ready", () => {
      this.stripeReady = true;
      this.ref.detectChanges();
    });
    this.paymentElement.on("change", (event) => {
      this.cardInputComplete = event.complete;
      this.cardErrors = event.error?.message || null;
    });
  }

  public changeCardOnFile() {
    this.changeCard = true;
    this.ref.detectChanges();
    this.setupStripeElements("changeCard");
  }

  public saveCardToPayOnNoShow = async () => {
    this.savingStripe = true;

    const { error: submitError } = await this.elements.submit();
    if (submitError) {
      this.savingStripe = false;
      this.cardErrors = submitError.message;
      this.ref.detectChanges();
      return;
    }

    try {
      const clientSecret = await this.stripeService
        .clientIntentSecret({
          clientID: this.client?.getID(),
          newClient: this.newStripeClient,
        })
        .toPromise();

      const result = await this.stripe.confirmSetup({
        elements: this.elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        this.savingStripe = false;
        if (result.error.code === "card_declined") {
          this.cardErrors = this.translateService.instant('STRIPE.CARD_DECLINED');
        } else {
          this.cardErrors = `${this.translateService.instant('STRIPE.CARD_ERROR_PREFIX')} ${result.error.message}`;
        }
        this.ref.detectChanges();
      } else if (result.setupIntent?.status === "succeeded") {
        this.onBookingSave.emit();
      }
    } catch (err) {
      this.savingStripe = false;
      this.cardErrors = `${this.translateService.instant('STRIPE.CARD_ERROR_PREFIX')} ${err.message || err}`;
      this.ref.detectChanges();
    }
  };
  public setupConfirmationSavedCard = async () => {
    this.savingStripe = true;
    const clientSecret = await this.stripeService
      .clientIntentSecret({
        clientID: this.client.getID(),
        newClient: this.newStripeClient,
      })
      .toPromise();
    const paymentMethods = await this.stripeService
      .getPaymentMethods({ clientID: this.client.getID() })
      .toPromise();
    try {
      const { setupIntent, error } = await this.stripe.confirmSetup({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethods["data"][0].id,
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      this.cardSetupConfirmationDone({ error, setupIntent });
    } catch (err) {
      this.savingStripe = false;
    }
  };

  public cardSetupConfirmationDone = ({ error, setupIntent }) => {
    if (error) {
      // Display error.message in your UI.
      this.savingStripe = false;
      this.cardErrors = `${this.translateService.instant('STRIPE.CARD_ERROR_PREFIX')} ${error.message}`;
    } else {
      if (setupIntent.status === "succeeded") {
        // The setup has succeeded. Display a success message. Send
        // setupIntent.payment_method to your server to save the card to a Customer
        // this.savingStripe = false;
        this.onBookingSave.emit();
      }
    }
  };

  public async chargeAfterSave() {
    const result = await this.stripeService
      .chargeSavedCard({
        clientID: this.client.getID(),
        amount: this.totalPrice * 100,
        currency: this.currency,
      })
      .toPromise();
  }
}
